/* B Free - PDP reviews fixes. Updated 10 Sep 2026.

   Three jobs:
   1. Set the exact loop distance per column so the marquee wraps seamlessly.
   2. Take over wheel and swipe inside the block so the reviews move while
      the page stays still — users can manually scroll through reviews on
      desktop (mouse wheel) and mobile (touch drag).
   3. Show customer photos on reviews, and make the Sort options that were
      never wired up actually work (With Photos, Most Helpful).
   4. Show a simple thank-you confirmation after a review or question is
      submitted, replacing whatever Stamped renders in the form slot.

   Loaded from sections/custom-liquid.liquid, only on the reviews section.
   TIDY-UP for whoever next edits assets/theme.js:
     - buildColumns() sets animationDuration from scrollHeight/2, which is half
       a gap short. This file corrects it.
     - buildCard() never renders review.reviewUserPhotos. This file adds it.
     - bfreeFetchStampedAPI() sends isWithPhotos=1. Stamped ignores that and
       returns everything. The value has to be the string "true".
     - bfreeApplySort() only handles "recent" and "highest-rating".
     - bfreeSetupManualScroll() toggles a .bfree-paused class that has no CSS
       rule anywhere. Dead code, safe to delete. */
(function () {
  'use strict';

  var PX_PER_SECOND = 30;
  var MIN_DURATION = 15;
  var RESUME_DELAY = 2000;

  /* Same public widget credentials theme.js already uses. */
  var API = 'https://stamped.io/api/widget/reviews';
  var API_KEY = 'pubkey-503131bs4fsU32W9jva35iQ3LeZitu';
  var STORE_ID = '6990';
  var PHOTO_BASE = 'https://cdn.stamped.io/uploads/photos/';

  var reduceMotion = !!(window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  /* ------------------------------------------------------------------
     1. Loop distance
     ------------------------------------------------------------------ */

  function colsOf(container) {
    return container.querySelectorAll('.bfree-review-col');
  }

  function travelOf(col) {
    var gap = parseFloat(getComputedStyle(col).rowGap) || 0;
    /* Cards are rendered twice, so half the content plus one gap is exactly
       one full set. That is the only distance that loops without a jump. */
    return (col.scrollHeight + gap) / 2;
  }

  function currentOffset(col) {
    var t = getComputedStyle(col).transform;
    if (!t || t === 'none') { return 0; }
    var m = t.match(/matrix3?d?\(([^)]+)\)/);
    if (!m) { return 0; }
    var p = m[1].split(',');
    var y = parseFloat(p.length > 6 ? p[13] : p[5]);
    return isNaN(y) ? 0 : -y;
  }

  function wrap(value, span) {
    return ((value % span) + span) % span;
  }

  function setTravel(col) {
    /* A hidden column measures 0, which would write a nonsense distance.
       Skip it and let the visibility hooks below re-run once it is shown. */
    if (!col.offsetParent) { return; }
    var travel = travelOf(col);
    if (travel < 50) { return; }
    var dur = Math.max(MIN_DURATION, travel / PX_PER_SECOND);
    if (col.style.animationDelay && !col.bfreeHeld) {
      col.style.animationDelay = (-(wrap(currentOffset(col), travel) / travel) * dur) + 's';
    }
    col.style.setProperty('--bfree-travel', travel + 'px');
    col.style.animationDuration = dur + 's';
  }

  function refresh(container) {
    var list = colsOf(container);
    for (var i = 0; i < list.length; i++) { setTravel(list[i]); }
  }

  function debounce(fn, ms) {
    var timer;
    return function () {
      clearTimeout(timer);
      timer = setTimeout(fn, ms);
    };
  }

  /* ------------------------------------------------------------------
     2. Wheel and swipe takeover — manual scrolling through reviews
     ------------------------------------------------------------------ */

  function applyOffset(col) {
    var travel = travelOf(col);
    if (travel < 50) { return; }
    /* Clamp offset so the user can scroll through the full review list and
       hit the bottom, rather than wrapping infinitely back to the top.
       parentElement is the overflow-clipped container; its clientHeight is
       the visible window height. */
    var visibleH = col.parentElement ? col.parentElement.clientHeight : 0;
    var maxOff = travel - visibleH;
    col.bfreeOffset = maxOff > 0
      ? Math.max(0, Math.min(col.bfreeOffset || 0, maxOff))
      : 0;
    col.style.transform = 'translateY(' + (-col.bfreeOffset) + 'px)';
  }

  function hold(container) {
    var list = colsOf(container);
    for (var i = 0; i < list.length; i++) {
      var col = list[i];
      if (!col.bfreeHeld) {
        col.bfreeOffset = currentOffset(col);
        col.bfreeHeld = true;
        col.style.animationName = 'none';
        applyOffset(col);
      }
    }
  }

  function nudge(container, delta) {
    var list = colsOf(container);
    for (var i = 0; i < list.length; i++) {
      list[i].bfreeOffset = (list[i].bfreeOffset || 0) + delta;
      applyOffset(list[i]);
    }
  }

  /* Hand control back to the CSS animation, starting from where we stopped.
     A negative animation-delay seeks the keyframe to that exact point. */
  function release(container) {
    var list = colsOf(container);
    for (var i = 0; i < list.length; i++) {
      var col = list[i];
      if (!col.bfreeHeld) { continue; }
      var travel = travelOf(col);
      var dur = Math.max(MIN_DURATION, travel / PX_PER_SECOND);
      col.style.transform = '';
      col.style.animationDelay = travel > 50
        ? (-((col.bfreeOffset || 0) / travel) * dur) + 's'
        : '0s';
      col.style.animationName = '';
      col.bfreeHeld = false;
    }
  }

  function attach(container) {
    if (!container || container.getAttribute('data-bfree-scroll') === '1') { return; }
    container.setAttribute('data-bfree-scroll', '1');

    var recalc = debounce(function () { refresh(container); }, 150);

    if (window.MutationObserver) {
      new MutationObserver(recalc).observe(container, { childList: true });
    }

    /* Photos finishing loading, or the block being revealed by a tab switch,
       both change the true column height — recalculate travel distance. */
    container.addEventListener('load', recalc, true);

    if (window.IntersectionObserver) {
      new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) { recalc(); }
        }
      }).observe(container);
    }

    document.addEventListener('click', function (e) {
      if (e.target && e.target.classList &&
          e.target.classList.contains('bfree-reviews-tab-btn')) { recalc(); }
    });

    refresh(container);

    if (reduceMotion) { return; }

    /* Set touch-action from JS so the block scrolls normally if JS fails.
       pan-x lets the browser handle horizontal swipes while our JS handles
       vertical drags to scroll through reviews. */
    container.style.touchAction = 'pan-x';

    /* Desktop: mouse wheel scrolls through the reviews. */
    container.addEventListener('wheel', function (e) {
      if (!e.deltaY) { return; }
      e.preventDefault();
      hold(container);
      nudge(container, e.deltaY);
    }, { passive: false });

    /* When the mouse leaves the reviews block, resume the auto-animation
       from wherever the user left off. */
    container.addEventListener('mouseleave', function () {
      release(container);
    });

    /* Mobile: touch drag scrolls through the reviews. */
    var lastY = null;
    var resumeTimer = null;

    container.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1) { return; }
      clearTimeout(resumeTimer);
      lastY = e.touches[0].clientY;
      hold(container);
    }, { passive: true });

    container.addEventListener('touchmove', function (e) {
      if (lastY === null || e.touches.length !== 1) { return; }
      var y = e.touches[0].clientY;
      var dy = lastY - y;
      lastY = y;
      if (!dy) { return; }
      e.preventDefault();
      nudge(container, dy);
    }, { passive: false });

    container.addEventListener('touchend', function () {
      lastY = null;
      clearTimeout(resumeTimer);
      resumeTimer = setTimeout(function () { release(container); }, RESUME_DELAY);
    }, { passive: true });

    window.addEventListener('load', recalc);
    window.addEventListener('resize', recalc);
  }

  /* ------------------------------------------------------------------
     3. Customer photos and the unwired Sort options
     ------------------------------------------------------------------ */

  function photoNames(review) {
    var raw = review && review.reviewUserPhotos;
    if (!raw) { return []; }
    return String(raw).split(',').map(function (s) { return s.trim(); })
      .filter(function (s) { return !!s; });
  }

  function patchBuildCard() {
    if (typeof window.buildCard !== 'function' || window.buildCard.bfreePatched) {
      return typeof window.buildCard === 'function';
    }
    var original = window.buildCard;
    var patched = function (review) {
      var card = original(review);
      var names = photoNames(review);
      if (!card || !names.length) { return card; }
      var box = document.createElement('div');
      box.className = 'bfree-review-photos' + (names.length > 1 ? ' is-multi' : '');
      names.forEach(function (name) {
        var img = document.createElement('img');
        img.loading = 'lazy';
        img.decoding = 'async';
        img.alt = 'Photo from ' + (review.author || 'a verified buyer');
        img.src = PHOTO_BASE + name;
        box.appendChild(img);
      });
      var footer = card.querySelector('.bfree-review-footer');
      if (footer) { card.insertBefore(box, footer); } else { card.appendChild(box); }
      return card;
    };
    patched.bfreePatched = true;
    window.buildCard = patched;
    return true;
  }

  function fullSet() {
    if (!window.bfreeFullReviewSet && window.bfreeCurrentReviews) {
      window.bfreeFullReviewSet = window.bfreeCurrentReviews;
    }
    return window.bfreeFullReviewSet || [];
  }

  function render(list) {
    var c = document.getElementById('bfree-reviews-columns');
    if (!c || typeof window.buildColumns !== 'function' || !list || !list.length) { return; }
    c.innerHTML = '';
    window.buildColumns(list);
  }

  function productId() {
    return window.meta && window.meta.product && window.meta.product.id;
  }

  function loadPhotoReviews(done) {
    if (window.bfreePhotoReviewSet) { done(window.bfreePhotoReviewSet); return; }
    var id = productId();
    if (!id) { done([]); return; }
    /* Stamped only filters to photo reviews when the value is the string
       "true". isWithPhotos=1 is ignored and returns everything. */
    var url = API + '?productId=' + encodeURIComponent(id) +
      '&apiKey=' + API_KEY + '&sId=' + STORE_ID +
      '&page=1&take=100&sortReviews=1&isWithPhotos=true';
    fetch(url)
      .then(function (r) { return r.json(); })
      .then(function (j) {
        window.bfreePhotoReviewSet = (j && j.data) || [];
        done(window.bfreePhotoReviewSet);
      })
      .catch(function () { done([]); });
  }

  function patchApplySort() {
    if (typeof window.bfreeApplySort !== 'function' || window.bfreeApplySort.bfreePatched) {
      return typeof window.bfreeApplySort === 'function';
    }
    var original = window.bfreeApplySort;
    var patched = function (selectEl) {
      var value = selectEl && selectEl.value;
      fullSet();
      if (value === 'with-photos') {
        loadPhotoReviews(function (list) {
          if (list.length) { render(list); }
        });
        return;
      }
      if (value === 'most-votes') {
        render(fullSet().slice().sort(function (a, b) {
          return (b.reviewVotesUp || 0) - (a.reviewVotesUp || 0);
        }));
        return;
      }
      /* Featured, Most Recent and Highest Rating stay with the original
         handler, but always from the complete set. */
      window.bfreeCurrentReviews = fullSet();
      original(selectEl);
    };
    patched.bfreePatched = true;
    window.bfreeApplySort = patched;
    return true;
  }

  /* theme.js defines these globals. Poll briefly in case it parses after us. */
  function patchWhenReady() {
    var tries = 0;
    var timer = setInterval(function () {
      tries++;
      var a = patchBuildCard();
      var b = patchApplySort();
      if ((a && b) || tries > 100) { clearInterval(timer); }
    }, 30);
  }

  /* If theme.js managed to render before we patched buildCard, the photos
     would be missing. Rebuild once, only if that actually happened. */
  function reconcileOnce() {
    var c = document.getElementById('bfree-reviews-columns');
    if (!c || c.getAttribute('data-bfree-photos-checked') === '1') { return; }
    if (!c.querySelector('.bfree-review-card')) { return; }
    c.setAttribute('data-bfree-photos-checked', '1');
    var list = fullSet();
    var expected = list.some(function (r) { return photoNames(r).length > 0; });
    if (expected && !c.querySelector('.bfree-review-photos')) { render(list); }
  }

  /* ------------------------------------------------------------------
     4. Thank-you confirmation after review or question submission
     ------------------------------------------------------------------ */

  function injectThankYouStyles() {
    if (document.getElementById('bfree-thankyou-style')) { return; }
    var s = document.createElement('style');
    s.id = 'bfree-thankyou-style';
    s.textContent =
      '.bfree-thankyou{' +
        'display:flex;flex-direction:column;align-items:center;' +
        'justify-content:center;padding:40px 24px;text-align:center;gap:14px;' +
      '}' +
      '.bfree-thankyou-icon{' +
        'color:#6b9e78;width:52px;height:52px;flex-shrink:0;' +
      '}' +
      '.bfree-thankyou-heading{' +
        'font-size:18px;font-weight:600;margin:0;' +
      '}' +
      '.bfree-thankyou-body{' +
        'font-size:14px;margin:0;opacity:0.65;' +
      '}';
    document.head.appendChild(s);
  }

  function showThankYou(slot, type) {
    injectThankYouStyles();
    var heading = type === 'review'
      ? 'Thank you for your review!'
      : 'Thank you for your question!';
    slot.innerHTML =
      '<div class="bfree-thankyou">' +
        '<svg class="bfree-thankyou-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">' +
          '<circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.5"/>' +
          '<path d="M8 12.5l2.5 2.5 5-5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
        '</svg>' +
        '<p class="bfree-thankyou-heading">' + heading + '</p>' +
        '<p class="bfree-thankyou-body">We appreciate you taking the time to share.</p>' +
      '</div>';
    /* Auto-dismiss after 8 seconds — only clear if our message is still
       showing (don't clobber anything Stamped may inject afterwards). */
    setTimeout(function () {
      if (slot.querySelector('.bfree-thankyou')) {
        slot.innerHTML = '';
      }
    }, 8000);
  }

  function watchFormSlot(slotId, type) {
    var slot = document.getElementById(slotId);
    if (!slot || !window.MutationObserver) { return; }

    /* Outer observer: waits for Stamped to inject a <form> into the slot. */
    new MutationObserver(function () {
      var form = slot.querySelector('form');
      if (!form || form._bfreeWatched) { return; }
      form._bfreeWatched = true;

      var submitted = false;
      var shown = false;

      function maybeShow() {
        if (!shown) {
          shown = true;
          showThankYou(slot, type);
        }
      }

      /* Track whether the user actually hit Submit (vs. just closing the form). */
      form.addEventListener('submit', function () {
        submitted = true;
        /* Fallback: if Stamped never removes the form from the slot
           (e.g. it renders its success message elsewhere), show our
           message after a short delay. */
        setTimeout(function () {
          if (submitted && !shown) { maybeShow(); }
        }, 2000);
      });

      /* Inner observer: fires when Stamped removes / replaces the form
         inside the slot after a successful submission. */
      new MutationObserver(function () {
        if (submitted && !slot.querySelector('form')) {
          /* Brief pause so Stamped finishes its own DOM cleanup first. */
          setTimeout(function () {
            if (!slot.querySelector('form')) { maybeShow(); }
          }, 150);
        }
      }).observe(slot, { childList: true, subtree: true });

    }).observe(slot, { childList: true, subtree: true });
  }

  function setupFormThankYou() {
    watchFormSlot('bfree-write-review-form-slot', 'review');
    watchFormSlot('bfree-ask-question-form-slot', 'question');
  }

  /* ------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------ */

  function init() {
    attach(document.getElementById('bfree-reviews-columns'));
    attach(document.getElementById('bfree-questions-columns'));
    setupFormThankYou();
    setTimeout(reconcileOnce, 1500);
    setTimeout(reconcileOnce, 4000);
  }

  patchWhenReady();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
